import { useEffect, useRef } from 'react';
import { useShareIntent } from 'expo-share-intent';

import { requestShareToChat } from '../deeplinks/pendingShareBridge';
import { normalizeShareIntent } from './incomingShare';

/**
 * Подписка приложения на системное «Поделиться» (стори 8.7).
 *
 * <p>Живёт в корне `App`, потому что содержимое приходит и в холодный старт (человек выбрал
 * SVYAZ в меню, приложение только поднимается), и в уже запущенное приложение. Показывать выбор
 * чата отсюда нечем — экранов ещё нет, — поэтому содержимое кладётся в буфер
 * ({@link ../deeplinks/pendingShareBridge}), а забирает его список чатов, когда смонтируется.
 *
 * <p><b>Про `resetOnBackground: false`.</b> Умолчание пакета сбрасывает содержимое, когда
 * приложение уходит в фон. Нам это вредно: между «поделиться» и выбором чата человек вполне
 * может выйти и вернуться, а на непринятом снимке это выглядело бы как потеря.
 */
export function useIncomingShareIntent(): void {
  const { hasShareIntent, shareIntent, resetShareIntent } = useShareIntent({
    resetOnBackground: false,
  });
  /**
   * Одно и то же содержимое не должно приходить дважды. Ключ — сам состав отправки, а не
   * ссылка на объект: хук пересоздаёт её на каждый рендер, и по ссылке сравнение бесполезно.
   */
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasShareIntent) {
      lastKeyRef.current = null;
      return;
    }
    const share = normalizeShareIntent(shareIntent);
    if (!share) {
      // Прислали то, что мы не отправляем (и не просили в системном меню). Забываем молча:
      // «поделиться» уже случилось, и держать пустой замок незачем.
      resetShareIntent();
      return;
    }
    const key = JSON.stringify([share.text, share.files.map((f) => f.uri)]);
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    requestShareToChat(share);
    // Нативный модуль своё дело сделал: дальше содержимое живёт в буфере приложения.
    resetShareIntent();
  }, [hasShareIntent, shareIntent, resetShareIntent]);
}
